import { io, Socket } from 'socket.io-client';

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // 30 seconds max

  connect(): void {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return;
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    const socketUrl = API_BASE_URL.replace('/api', '');


    this.socket = io(socketUrl, {
      auth: {
        token: token
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000
    });

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
    });

    this.socket.on('disconnect', (reason) => {
      // Socket disconnected
    });

    this.socket.on('connect_error', (error: any) => {
      if (error.message.includes('Unauthorized') || error.message.includes('401')) {
        return; // Don't retry until user re-authenticates
      }

      this.handleReconnect();
    });

    this.socket.on('error', (error) => {
      // Socket error handled silently
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(
        this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
        this.maxReconnectDelay
      );
      
      setTimeout(() => {
        if (this.socket) {
          this.socket.connect();
        }
      }, delay);
    } else {
      // Max reconnection attempts reached
    }
  }

  // Event listeners
  onNotificationNew(callback: (notification: any) => void): void {
    if (this.socket) {
      this.socket.on('notification:new', callback);
    }
  }

  onNotificationDeleted(callback: (data: { id: string }) => void): void {
    if (this.socket) {
      this.socket.on('notification:deleted', callback);
    }
  }

  onUnreadCountUpdate(callback: (data: { count: number }) => void): void {
    if (this.socket) {
      this.socket.on('notification:unreadCount', callback);
    }
  }

  // Remove event listeners
  offNotificationNew(callback: (notification: any) => void): void {
    if (this.socket) {
      this.socket.off('notification:new', callback);
    }
  }

  offNotificationDeleted(callback: (data: { id: string }) => void): void {
    if (this.socket) {
      this.socket.off('notification:deleted', callback);
    }
  }

  offUnreadCountUpdate(callback: (data: { count: number }) => void): void {
    if (this.socket) {
      this.socket.off('notification:unreadCount', callback);
    }
  }

  // Check if connected
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Get socket instance (for advanced usage)
  getSocket(): Socket | null {
    return this.socket;
  }
}

// Export singleton instance
export const socketService = new SocketService();
export default socketService;
